import { CriteriaModifierType } from 'api-models'
import { ScopeType, SourceType } from 'api-models/dist/advertiser/rule'

export const SCOPE_OPTIONS = [
	{
		value: ScopeType.WEBSITE,
		label: 'This website',
		level: 'website',
		show_options: false
	},
	{
		value: ScopeType.ACCOUNT,
		label: 'Selected ad accounts',
		level: 'website',
		show_options: true
	},
	{
		value: ScopeType.ACCOUNT,
		label: 'This PPC account',
		level: 'ad-account',
		show_options: false
	},
	{
		value: ScopeType.CAMPAIGNS,
		label: 'Selected campaigns',
		level: 'ad-account',
		show_options: true
	},
	{
		value: ScopeType.CAMPAIGNS,
		label: 'This campaign',
		level: 'campaign',
		show_options: false
	}
]

export const TIME_PERIOD_OPTIONS = ['minute', 'hour', 'day']

export const FOCUS_OPTIONS = [
	{ value: SourceType.VISITOR, label: 'Visitor' },
	{ value: SourceType.IP, label: 'IP address' },
	{ value: SourceType.RANGE, label: 'IP range' },
	{ value: SourceType.PLACEMENT, label: 'Placement' },
	{ value: SourceType.DEVICE, label: 'Device' }
]

export const CLICK_STATUS_OPTIONS = ['GOOD', 'SUSPICIOUS', 'BAD']

export const PERIOD_OPTIONS = [
	{ value: 'second', label: 'seconds' },
	{ value: 'minute', label: 'minutes' },
	{ value: 'hour', label: 'hours' },
	{ value: 'day', label: 'days' }
]

export const ADDITIONAL_CRITERIA_OPTIONS = [
	{
		label: 'Click has bounced',
		key: 'no_device',
		type: CriteriaModifierType.NO_DEVICE,
		tooltip:
			'Clicks never reached, or spent less than a second on the landing page.',
		disabledTooltip:
			'This criteria is not available when using Device-based trigger. Select Visitor instead.',
		hasPrefix: false,
		prefixOptions: [],
		valueType: 'boolean',
		options: []
	},
	{
		label: 'Click was previously detected as bad',
		key: 'bad_clicks',
		type: CriteriaModifierType.BAD_CLICKS,
		tooltip:
			'Clicks already have BAD status due to other protection settings and rules.',
		hasPrefix: false,
		prefixOptions: [],
		valueType: 'boolean',
		options: []
	},
	{
		label: 'Detected as VPN or other proxy',
		key: 'proxy',
		type: CriteriaModifierType.PROXY,
		tooltip: 'Clicks that were made using a VPN or a similar proxy.',
		hasPrefix: false,
		prefixOptions: [],
		valueType: 'boolean',
		options: []
	},
	{
		label: 'Detected threat level',
		key: 'threat_level',
		type: CriteriaModifierType.THREAT_LEVEL,
		tooltip:
			'Clicks have specific threat level detected by 3rd party intelligence tools.',
		hasPrefix: true,
		prefixOptions: ['is', 'is not'],
		valueType: 'array',
		options: ['low', 'medium', 'high']
	},
	{
		label: 'Detected IP address type',
		key: 'ip_usage',
		type: CriteriaModifierType.IP_USAGE,
		tooltip: 'Clicks that were made from a specific type of IP address.',
		hasPrefix: true,
		prefixOptions: ['is', 'is not'],
		valueType: 'array',
		options: ['Data center', 'Business', 'Residential']
	},
	{
		label: 'Detected device type',
		key: 'device_type',
		type: CriteriaModifierType.DEVICE_TYPE,
		tooltip: 'Clicks that were made from a specific type of device.',
		hasPrefix: true,
		prefixOptions: ['is', 'is not'],
		valueType: 'array',
		options: ['mobile', 'tablet', 'computer']
	},
	{
		label: 'Source country',
		key: 'country',
		type: CriteriaModifierType.COUNTRY,
		tooltip:
			'Clicks that did (or did not) originate from a specific list of countries.',
		hasPrefix: true,
		prefixOptions: ['is', 'is not'],
		options: [],
		valueType: 'country'
	},
	{
		label: 'Internet provider (ISP)',
		key: 'isp',
		type: CriteriaModifierType.ISP,
		tooltip: 'Match the internet provider detected for the click.',
		hasPrefix: true,
		prefixOptions: ['is', 'is not'],
		options: [],
		valueType: 'network'
	},
	{
		label: 'Network (ASN)',
		key: 'asn',
		type: CriteriaModifierType.ASN,
		tooltip:
			'Match the autonomous system number of the network used for the click.',
		hasPrefix: true,
		prefixOptions: ['is', 'is not'],
		options: [],
		valueType: 'network'
	},
	{
		label: 'Search keyword',
		key: 'keyword_text',
		type: CriteriaModifierType.SEARCH_KEYWORD,
		tooltip:
			'Clicks that did (or did not) come from a specified keyword. Supports only Google Ads clicks.',
		hasPrefix: true,
		prefixOptions: ['is', 'is not'],
		options: [],
		valueType: 'keywords'
	},
	{
		label: 'Detected website interaction level',
		key: 'user_interaction',
		type: CriteriaModifierType.INTERACTION,
		tooltip: 'Clicks that were detected with specific user interaction levels.',
		hasPrefix: true,
		prefixOptions: ['is', 'is not'],
		valueType: 'array',
		options: ['none', 'low', 'medium', 'high'],
		feature_tier: 'standard'
	},
	{
		label: 'Detected bot probability level',
		key: 'bot_probability',
		type: CriteriaModifierType.BOT_PROBABILITY,
		tooltip: 'Clicks that were detected with specific bot probability levels.',
		hasPrefix: true,
		prefixOptions: ['is', 'is not'],
		valueType: 'array',
		options: ['low', 'medium', 'high'],
		feature_tier: 'standard'
	},
	{
		label: 'Time spent on website',
		key: 'time',
		type: CriteriaModifierType.TIME,
		tooltip:
			'Clicks that were measured to have spent specific time on the website.',
		hasPrefix: true,
		prefixOptions: ['is more than', 'is less than'],
		valueType: 'number',
		options: []
	},
	{
		label: 'Number of page views',
		key: 'page_views',
		type: CriteriaModifierType.PAGE_VIEWS,
		tooltip: 'Clicks that made specific number of page views.',
		hasPrefix: true,
		prefixOptions: ['is more than', 'is less than'],
		valueType: 'number',
		options: []
	},
	{
		label: 'Conversion recorded',
		key: 'conversion',
		type: CriteriaModifierType.CONVERSION,
		tooltip: 'Clicks that have a conversion event attributed to them.',
		hasPrefix: false,
		prefixOptions: [],
		valueType: 'boolean',
		options: ['all', 'with label'],
		feature_tier: 'pro'
	},
	{
		label: 'Conversion not recorded',
		key: 'not_conversion',
		type: CriteriaModifierType.NOT_CONVERSION,
		tooltip: 'Clicks that don’t have a conversion attributed to them.',
		hasPrefix: false,
		prefixOptions: [],
		valueType: 'boolean',
		options: [],
		feature_tier: 'pro'
	}
]

export const THRESHOLD_OPTIONS = [
	{ value: 'all', label: 'All clicks' },
	{ value: 'percentage', label: 'Percentage of clicks' }
]
